import { i18n } from 'i18next';
import { Query, queryOps } from 'system/query/Query';
import { QueryInfo } from 'system/query/QueryInfo';
import { QueryLeafNode } from 'system/query/QueryLeafNode';
import { WidgetRequest, WidgetResponseItem } from 'system/widget/Widget';
import { getFieldToColor } from 'theme/KeyToInfegyColor';
import { formatNestedLabel } from 'system/Utilities';

export interface ResponseBucket {
  _key: string;
  _label: string;
  _count: number;
  percent: number;
}

export interface ResponseData {
  _field_label: string;
  _field_id: string;
  _buckets: ResponseBucket[];
}

export interface CategoryWidgetCategory {
  key: string;
  label: string;
  bucketLabel?: string;
  queryLabel?: string;
  count: number;
  percent: number;
  color?: string;
  drilldownQuery?: Query;
}

export interface CategoryWidgetData {
  fieldId: string;
  fieldLabel: string;

  categoryRows: CategoryWidgetCategory[];
}

export function MakeCategorySegmentsFromResponse(apiResponses: WidgetResponseItem[] | WidgetResponseItem, queryInfo:any, theme:any, i18n:i18n):CategoryWidgetData | undefined{
  if (Array.isArray(apiResponses) && apiResponses.length > 1) {
    return MakeCategorySegmentsFromMultiResponses(apiResponses, queryInfo, theme, i18n);
  } else {
    let apiResponse = apiResponses;
    if (Array.isArray(apiResponses)) {
      if (apiResponses.length === 0) {
        return undefined;
      }
      apiResponse = apiResponses[0];
    }
    // We assume that the response is for a single query, so we can directly pass
    return MakeCategorySegmentsFromSingleResponse(apiResponse as WidgetResponseItem, queryInfo, theme);
  }
}

// This function is used for Multi-Query, to create segments for each query
export function MakeCategorySegmentsFromMultiResponses(apiResponses:WidgetResponseItem[], queryInfo:any, theme:any, i18n:i18n):CategoryWidgetData {
  const firstResponse = apiResponses[0].responseBody;

  if (!firstResponse || Object.keys(firstResponse).length === 0) {
    return {
      fieldId: "",
      fieldLabel: "",
      categoryRows: []
    };
  }

  const firstResponseKey = Object.keys(firstResponse).filter(key => key !== "_total")[0];
  const firstResponseData:ResponseData = firstResponse[firstResponseKey] as ResponseData;
  const fieldId = firstResponseData?._field_id;
  let bucketLabel = "";

  const displayRows = apiResponses.map((apiResponse:any, index:number) => {
    const responseBody = apiResponse.responseBody;
    const color = theme.colors.namedColors[index % theme.colors.namedColors.length];

    const responseData:ResponseData = responseBody[firstResponseKey] as ResponseData;
    const buckets = responseData?._buckets || [];

    if (!buckets || buckets.length === 0) {
      return undefined;
    }

    const firstBucket = buckets[0];

    bucketLabel = formatNestedLabel(firstBucket?._label);
    const category: CategoryWidgetCategory = {
      key: firstBucket?._key || "",
      label: apiResponse.queryDisplayTitle || bucketLabel,
      bucketLabel: bucketLabel,
      queryLabel: apiResponse.queryDisplayTitle,
      percent: firstBucket?.percent || 0,
      count: firstBucket?._count || 0,
      color: getFieldToColor(fieldId, firstBucket?._key) !== undefined ? theme.colors.namedColors[getFieldToColor(fieldId, firstBucket?._key)!] : color,
    }
    if (firstBucket) {
      category.drilldownQuery = { query: { op: queryOps.contains, fields: [fieldId], value: firstBucket._key } as QueryLeafNode};
    }
    return category;
  }).filter((item): item is CategoryWidgetCategory => item !== undefined);

  let fieldLabel = `${firstResponseData?._field_label} ${bucketLabel}`;

  // If there is more than one response and any of the categories don't match,
  // We know there is no specified bucket, and we should adjust labels accordingly.
  if (displayRows.length > 1) {
    const uniqueLabels = Array.from(new Set(displayRows.map(row => row.bucketLabel)));

    if (uniqueLabels.length > 1) {
      displayRows.forEach(row => {
        if (row.queryLabel) {
          row.label = `${row.queryLabel}: ${row.bucketLabel}`;
        } else {
          row.label = row.bucketLabel || "";
        }
      });
      if (i18n) {
        fieldLabel = `${i18n.t("top")} ${firstResponseData?._field_label || ""}`;
      } else {
        fieldLabel = `Top ${firstResponseData?._field_label || ""}`;
      }
    }
  }

  return {
    fieldId: fieldId,
    fieldLabel: fieldLabel,
    categoryRows: displayRows || []
  }
}

// This function is used for Single-Query, to create segments for categories in a single query
export function MakeCategorySegmentsFromSingleResponse(apiResponse:WidgetResponseItem, queryInfo:any, theme:any):CategoryWidgetData {
  const responseBody = apiResponse.responseBody;

  if (!responseBody || Object.keys(responseBody).length === 0) {
    return {
      fieldId: "",
      fieldLabel: "",
      categoryRows: []
    };
  }

  const responseKey = Object.keys(responseBody).filter(key => key !== "_total")[0];
  const responseData:ResponseData = responseBody[responseKey] as ResponseData;
  const fieldId = responseData?._field_id;

  const displayRows = responseData?._buckets?.map((row: ResponseBucket, index:number) => {
    const color = theme.colors.namedColors[index % theme.colors.namedColors.length];
    return {
      key: row?._key,
      label: formatNestedLabel(row?._label),
      bucketLabel: formatNestedLabel(row?._label),
      queryLabel: apiResponse.queryDisplayTitle,
      percent: row?.percent,
      count: row?._count,
      color: getFieldToColor(fieldId, row._key) !== undefined ? theme.colors.namedColors[getFieldToColor(fieldId, row._key)!] : color,
      drilldownQuery: new Query({ query: { op: "contains", fields: [fieldId], value: row._key } })
    } as CategoryWidgetCategory;
  });

  return {
    fieldId: fieldId,
    fieldLabel: responseData?._field_label,
    categoryRows: displayRows
  }
}

export function BuildDefaultCategoryWidgetRequestBodyFunction(defaultWidgetRequest: WidgetRequest): (queryInfo: QueryInfo) => WidgetRequest | undefined  {
  return function BuildDefaultCategoryWidgetRequests(queryInfo: QueryInfo): any {
    const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = { aggs: {} };
    const multiRequestInitBody = { aggs: {} };

    const foundKeywordFields = queryInfo.fields?.filter((field:QueryField) => {
      const numberOfOptions = parseInt(field?.options?.length + "");
      return ["keyword", "keywords"].includes(field.type) && numberOfOptions;
    }) || false;

    if (foundKeywordFields?.length) {
      // Chose sentiment if available, otherwise choose the first keyword field
      const foundKeywordField = foundKeywordFields.find((field:QueryField) => {
        return field.id === "sentiment";
      }) || foundKeywordFields[0];

      const firstOption = foundKeywordField?.options?.[0];
      if (firstOption && firstOption.id && firstOption.label) {
        requestInitBody.aggs = {
          [foundKeywordField.id]: {
            "op":"keyword",
            "field": foundKeywordField.id,
            "aggs": { "percent": { "op": "percent", "of_total": true }  }
          }
        };
        multiRequestInitBody.aggs = {
          [firstOption.label]: {
            "op":"keyword",
            "field": foundKeywordField.id,
            "filter": [firstOption.id],
            "aggs": { "percent": { "op": "percent", "of_total": true }  }
          }
        };
      }
    }

    if (Object.keys(requestInitBody.aggs).length) {
      widgetRequest.requestInit = { body: requestInitBody };
      widgetRequest.multiRequestInit = { body: multiRequestInitBody };
      return widgetRequest;
    }

    return;
  }
}